/**
 * UserFilters Component
 *
 * Filter drawer for the user list page.
 * Provides role filter, status filters, and date range filtering.
 * Search is handled client-side in the DataTable component.
 */

import { useMemo } from 'react';
import { IconFilterOff } from '@tabler/icons-react';
import {
  Button,
  Stack,
  Select,
  TextInput,
  Text,
  Divider,
} from '../../../../components/ui';
import type { UserFiltersProps } from '../types';

export function UserFilters({
  filters,
  roles,
  onFilterChange,
  onClearFilters,
  loading = false,
}: UserFiltersProps) {
  // Prepare role options for Select component
  const roleOptions = useMemo(() => {
    return roles.map((role) => ({
      value: role.name!,
      label: role.name!,
    }));
  }, [roles]);

  // Status filter options
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  // Email confirmed filter options
  const emailConfirmedOptions = [
    { value: 'all', label: 'All' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'unconfirmed', label: 'Unconfirmed' },
  ];

  // Locked out filter options
  const lockedOutOptions = [
    { value: 'all', label: 'All' },
    { value: 'locked', label: 'Locked' },
    { value: 'unlocked', label: 'Unlocked' },
  ];

  return (
    <Stack gap="lg">
      {/* Filter Section Header */}
      <Text size="sm" c="dimmed">
        Apply filters to refine the user list. Search is available in the table above.
      </Text>

      <Divider />

      {/* Role Filter */}
      <Select
        label="Role"
        placeholder="All roles"
        value={filters.selectedRole}
        onChange={(value) => onFilterChange({ selectedRole: value })}
        data={roleOptions}
        clearable
        disabled={loading}
        aria-label="Filter by role"
      />

      {/* Status Filter */}
      <Select
        label="Status"
        placeholder="All statuses"
        value={filters.statusFilter}
        onChange={(value) =>
          onFilterChange({ statusFilter: value as 'all' | 'active' | 'inactive' })
        }
        data={statusOptions}
        disabled={loading}
        aria-label="Filter by status"
      />

      {/* Email Confirmed Filter */}
      <Select
        label="Email Status"
        placeholder="All"
        value={filters.emailConfirmedFilter}
        onChange={(value) =>
          onFilterChange({
            emailConfirmedFilter: value as 'all' | 'confirmed' | 'unconfirmed',
          })
        }
        data={emailConfirmedOptions}
        disabled={loading}
        aria-label="Filter by email confirmation status"
      />

      {/* Locked Out Filter */}
      <Select
        label="Lock Status"
        placeholder="All"
        value={filters.lockedOutFilter}
        onChange={(value) =>
          onFilterChange({ lockedOutFilter: value as 'all' | 'locked' | 'unlocked' })
        }
        data={lockedOutOptions}
        disabled={loading}
        aria-label="Filter by lock status"
      />

      <Divider label="Date Range" labelPosition="center" />

      {/* Created From Date */}
      <TextInput
        label="Created From"
        type="date"
        value={filters.createdFrom}
        onChange={(e) => onFilterChange({ createdFrom: e.currentTarget.value })}
        disabled={loading}
        aria-label="Filter by creation date from"
      />

      {/* Created To Date */}
      <TextInput
        label="Created To"
        type="date"
        value={filters.createdTo}
        onChange={(e) => onFilterChange({ createdTo: e.currentTarget.value })}
        disabled={loading}
        aria-label="Filter by creation date to"
      />

      <Divider />

      {/* Clear Filters Button */}
      <Button
        variant="light"
        color="gray"
        leftSection={<IconFilterOff size={16} />}
        onClick={onClearFilters}
        disabled={loading}
        fullWidth
      >
        Clear All Filters
      </Button>
    </Stack>
  );
}
